import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { searchRocketReach } from 'services/rocketReach'
import { getErrorMessage } from 'constants/rocketReachErrors'
import usePagination from 'hooks/usePagination'
import { useNotification } from 'components/Notification'

// Location & Industry is a single combined mode; every other mode is one
// identity/company term. Centralized here so the query key and the actual
// request body can never drift apart.
// A lone space is truthy in JS but renders as visually empty, so every check
// against a user-typed field must trim before testing for content.
const isBlank = value => !value || !value.trim()

const buildSearchPayload = searchForm => {
  const { searchBy, searchTerm, searchKeywords, location, industry, jobChangeSignal } = searchForm

  return {
    ...(searchBy === 'location_industry'
      ? !isBlank(location) && !isBlank(industry) && { location, industry }
      : searchBy && !isBlank(searchTerm) && { [searchBy]: searchTerm }),
    ...(!isBlank(searchKeywords) && { keywords: searchKeywords }),
    ...(searchBy === 'employer' && !isBlank(jobChangeSignal) && { jobChangeSignal }),
  }
}

/**
 * A search needs its mode's required field(s): a complete identity/company
 * term, or both location and industry together. This mirrors the backend's
 * at_least_one_search_param rule.
 */
export const hasPrimarySearchField = searchForm => {
  const { searchBy, location, industry, searchTerm } = searchForm

  if (searchBy === 'location_industry') {
    return !isBlank(location) && !isBlank(industry)
  }
  return Boolean(searchBy) && !isBlank(searchTerm)
}

const getSearchQueryKey = (searchForm, pagination) => [
  'rocketReach',
  'search',
  {
    pageOffset: pagination.pageOffset,
    pageSize: pagination.pageSize,
    ...buildSearchPayload(searchForm),
  },
]

/**
 * RocketReach search hook - handles search functionality only
 * @returns {object} - search results and search controls
 */
const useRocketReachSearch = () => {
  const queryClient = useQueryClient()
  const { pagination, handlePageChange, handleRowsChange } = usePagination()
  const { createNotification } = useNotification()

  const [searchForm, setSearchForm] = useState({
    searchBy: '',
    searchTerm: '',
    searchKeywords: '',
    location: '',
    industry: '',
    jobChangeSignal: '',
  })
  const [lastErrorMessage, setLastErrorMessage] = useState(null)

  // Search query
  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    isError: isSearchError,
  } = useQuery({
    queryKey: getSearchQueryKey(searchForm, pagination),
    queryFn: () =>
      searchRocketReach({
        pageOffset: pagination.pageOffset,
        pageSize: pagination.pageSize,
        ...buildSearchPayload(searchForm),
      }),
    initialData: {
      data: [],
      meta: {
        itemCount: 0,
      },
    },
    enabled: hasPrimarySearchField(searchForm),
    retry: false,
  })

  // Handle search success/error with useEffect
  useEffect(() => {
    if (searchResult && searchResult.error) {
      const errorMessage = getErrorMessage(searchResult.error)

      if (errorMessage !== lastErrorMessage) {
        setLastErrorMessage(errorMessage)
        createNotification({
          message: errorMessage,
          variant: 'error',
        })
      }
    } else if (searchResult && searchResult.data) {
      if (lastErrorMessage) {
        setLastErrorMessage(null)
      }
    }
  }, [searchResult, lastErrorMessage, createNotification])

  const handleSetPage = page => {
    handlePageChange(page)
  }

  const handleSetRowsPerPage = pageSize => {
    handleRowsChange(pageSize.rowsPerPage)
  }

  const handleSearch = newSearchForm => {
    setSearchForm(newSearchForm)

    // Reset pagination when search changes
    handlePageChange(0)

    // Invalidate the search query to force a new fetch since handleSearch is called on search button click otherwise it wont fetch new data
    queryClient.invalidateQueries({ queryKey: getSearchQueryKey(newSearchForm, pagination) })
  }

  return {
    state: {
      data: searchResult?.data || [],
      total: searchResult?.meta?.itemCount || 0,
      page: pagination.page,
      rowsPerPage: pagination.pageSize,
      isLoading: isSearchLoading || isSearchFetching,
      failed: isSearchError || (searchResult && searchResult.error),
    },
    setPage: handleSetPage,
    setRowsPerPage: handleSetRowsPerPage,
    handleSearch,
  }
}

export default useRocketReachSearch
